import { forwardRef } from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { LoadingSpinner } from './LoadingSpinner';

// ============================================
// Button Component
// ============================================

/**
 * Button - Herbruikbare button component volgens design system.
 * 
 * Variants: primary, secondary, outline, ghost, danger
 * Sizes: sm, md, lg
 * 
 * Gebruik:
 * ```tsx
 * <Button variant="primary" size="md" onClick={handleClick}>
 *   Submit
 * </Button>
 * 
 * <Button variant="danger" isLoading={true}>
 *   Verwijderen
 * </Button>
 * ```
 */

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

// Variant classes volgens design system (02_DESIGN_SYSTEM.md)
const variantClasses = {
  primary: 'bg-primary-500 text-white hover:bg-primary-600 active:bg-primary-700 disabled:bg-primary-300',
  secondary: 'bg-secondary-100 text-gray-700 border border-primary-500 hover:bg-secondary-200 active:bg-secondary-300 disabled:bg-gray-100',
  outline: 'bg-transparent text-primary-500 border border-primary-500 hover:bg-primary-50 active:bg-primary-100 disabled:border-primary-300 disabled:text-primary-400',
  ghost: 'bg-transparent text-gray-600 hover:bg-gray-100 active:bg-gray-200 disabled:text-gray-400',
  danger: 'bg-error-500 text-white hover:bg-error-600 active:bg-error-700 disabled:bg-error-300',
  success: 'bg-success-500 text-white hover:bg-success-600 active:bg-success-700 disabled:bg-success-300',
};

// Size classes volgens design system
const sizeClasses = {
  sm: 'px-2 py-1 text-sm gap-1 min-w-[100px]',
  md: 'px-4 py-2 text-sm gap-2 min-w-[120px]',
  lg: 'px-6 py-3 text-base gap-2 min-w-[140px]',
};

// Icon size mapping
const iconSizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      className,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const baseClasses = [
      'inline-flex items-center justify-center',
      'rounded-lg font-medium transition-colors',
      'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed',
    ].join(' ');

    const iconSize = iconSizeClasses[size];

    return (
      <button
        ref={ref}
        className={twMerge(
          clsx(
            baseClasses,
            variantClasses[variant],
            sizeClasses[size],
            className
          )
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <LoadingSpinner size={size} className="text-current" />
        ) : (
          <>
            {leftIcon && <span className={iconSize}>{leftIcon}</span>}
            {children}
            {rightIcon && <span className={iconSize}>{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
